import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminAxios } from "@/lib/adminAuth";
import { useAdmin } from "@/context/AdminContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ChevronLeft, Loader2, Mail, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
      {label}
    </p>
    <div className="text-sm text-slate-900">
      {value || <span className="text-slate-300">—</span>}
    </div>
  </div>
);

const AdminContactDetailPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { deleteContact } = useAdmin();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await adminAxios().get(`/admin/contacts/${id}`);
        setContact(res.data);
      } catch {
        toast.error("Contact introuvable.");
        nav("/admin/contacts");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, nav]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
      </div>
    );
  }

  if (!contact) return null;

  const fullName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim();

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <button
          onClick={() => nav("/admin/contacts")}
          className="flex items-center gap-1 hover:text-slate-900 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" /> Contacts
        </button>
        <span>/</span>
        <span className="text-slate-900 font-medium">{fullName}</span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-extrabold text-2xl text-slate-900">
            {fullName}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {new Date(contact.created_at).toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
        <div className="flex gap-3">
          {contact.email && (
            <a
              href={`mailto:${contact.email}?subject=Suite à votre message -BIONET SOLUTIONS`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-blue text-white text-sm font-medium hover:bg-brand-blue/90 transition-colors"
            >
              <Mail className="h-4 w-4" /> Répondre par email
            </a>
          )}
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-green text-white text-sm font-medium hover:bg-brand-green/90 transition-colors"
            >
              <Phone className="h-4 w-4" /> Appeler
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <p className="font-semibold text-slate-900 text-sm">Coordonnées</p>
          <Field label="Nom complet" value={fullName} />
          <Field
            label="Email"
            value={
              contact.email && (
                <a
                  href={`mailto:${contact.email}`}
                  className="text-brand-blue hover:underline"
                >
                  {contact.email}
                </a>
              )
            }
          />
          <Field
            label="Téléphone"
            value={
              contact.phone && (
                <a
                  href={`tel:${contact.phone}`}
                  className="text-brand-blue hover:underline"
                >
                  {contact.phone}
                </a>
              )
            }
          />
          <Field label="Ville" value={contact.city} />
          <Field label="Service demandé" value={contact.service_type} />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="font-semibold text-slate-900 text-sm mb-4">Message</p>
          <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {contact.message || "—"}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          variant="outline"
          className="rounded-full border-red-200 text-red-500 hover:bg-red-50"
          onClick={() => setConfirmDelete(true)}
          data-testid="delete-contact-btn"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Supprimer ce contact
        </Button>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer ce contact ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Cette action est irréversible.</p>
          <div className="flex gap-3 justify-end mt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmDelete(false)}>Annuler</Button>
            <Button
              className="rounded-full bg-red-500 hover:bg-red-600 text-white"
              onClick={async () => { await deleteContact(id); nav("/admin/contacts"); }}
              data-testid="confirm-delete-contact-detail"
            >
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContactDetailPage;
